import { APPOINTMENT_REMOTE_ID } from '@avanta/shared/constants/appointments';
import { type FieldValues, useFormContext, type UseFormSetValue } from 'react-hook-form';
import { AppointmentType } from 'types/appointments';
import { AppointmentsReservePayload } from '../types';

interface UseSpecialityStepFormReturn {
  specialityBooked: number | undefined;
  contactMethod: string | undefined;
  city: string | undefined;
  backofficeCenterId: string | undefined;
  isDisabled: boolean;
  setValue: UseFormSetValue<FieldValues>;
  handleOnCityChange: (value: string) => void;
  handleOnSpecialityChange: (value: number) => void;
  handleOnContactMethodChange: (value: string) => void;
}

export const useSpecialityStepForm = (): UseSpecialityStepFormReturn => {
  const { setValue, watch } = useFormContext();
  const [specialityBooked, contactMethod, city, backofficeCenterId] = watch([
    AppointmentsReservePayload.specialityBooked,
    AppointmentsReservePayload.contactMethod,
    AppointmentsReservePayload.city,
    AppointmentsReservePayload.backofficeCenterId,
  ]);

  const isDisabled =
    !specialityBooked ||
    !contactMethod ||
    (contactMethod === AppointmentType.InPerson && (!city || !backofficeCenterId));

  const handleOnCityChange = (value: string): void => {
    setValue(AppointmentsReservePayload.city, value);
    setValue(AppointmentsReservePayload.backofficeCenterId, '');
  };

  const handleOnSpecialityChange = (value: number): void => {
    const nextValue = specialityBooked === value ? undefined : value;
    setValue(AppointmentsReservePayload.specialityBooked, nextValue);
  };

  const handleOnContactMethodChange = (value: string): void => {
    const isAlreadySelected = contactMethod === value;

    setValue(AppointmentsReservePayload.contactMethod, isAlreadySelected ? undefined : value);
    setValue(AppointmentsReservePayload.backofficeCenterId, '');
    setValue(AppointmentsReservePayload.city, '');

    if (!isAlreadySelected && (value === AppointmentType.Video || value === AppointmentType.Chat)) {
      setValue(AppointmentsReservePayload.backofficeCenterId, APPOINTMENT_REMOTE_ID);
    }
  };

  return {
    specialityBooked,
    contactMethod,
    city,
    backofficeCenterId,
    isDisabled,
    setValue,
    handleOnCityChange,
    handleOnSpecialityChange,
    handleOnContactMethodChange,
  };
};
